import { ipaToFeatures, IpaType, isTone } from "../phonemes/ipa";
import { PhonemeItem } from "../phonemes/segmentPhonemes";
import { ipaToSynthSymbols, SynthTones } from "./exact";

/** change any tones into the subset supported by synth & distribute across diphthongs */
export function convertTones(phonemes: PhonemeItem[]): PhonemeItem[] {
  const hasTones = phonemes.some(p => isTone(p.phoneme));
  if (!hasTones) {
    return phonemes;
  }

  const converted = phonemes.map(pi => {
    if (!isTone(pi.phoneme)) {
      return pi;
    }
    const phoneme = convertTone(pi.phoneme);
    return { ...pi, phoneme };
  });
  const moved = moveTones(converted);
  const distributed = distributeTones(moved);
  return distributed;
}

/** convert an arbitrary list of 1-3 tones into the subset supported by synth */
function convertTone(tone: IpaType): IpaType {
  if (ipaToSynthSymbols[tone] !== undefined) {
    return tone;  // supported tone
  }

  const ns = asNumbers(tone);
  return ns.length === 2 ? twoTones(ns, tone) : threeTones(ns, tone);
}

function twoTones(ns: number[], tone: IpaType): IpaType {
  if (ns[0] < ns[1]) {
    // rising
    if ((ns[0] === 2 && ns[1] === 3) || (ns[0] === 1 && ns[1] === 2)) {
      return SynthTones.T23;
    } else if ((ns[0] === 3 && ns[1] === 5) || (ns[0] === 4 && ns[1] === 5)) {
      return SynthTones.T35;
    }
    // small or big
    return ns[1] - ns[0] <= 2 ? SynthTones.RiseSmall : SynthTones.RiseBig;
  } else if (ns[0] > ns[1]) {
    // falling
    if ((ns[0] === 3 && ns[1] === 2) || (ns[0] === 2 && ns[1] === 1)) {
      return SynthTones.T32;
    }
    // small or big
    return ns[0] - ns[1] <= 2 ? SynthTones.FallSmall : SynthTones.FallBig;;
  }
  return tone[0];
}

function threeTones(ns: number[], tone: IpaType): IpaType {
  // one small & one large, one rise & one fall
  if (ns[0] > ns[1] && ns[1] < ns[2] && ns[0] + 2 < ns[2]) {
    return SynthTones.T214;
  }

  if (ns[0] <= ns[1] && ns[1] <= ns[2] && ns[0] < ns[2]) {
    // all rising - small or big
    return ns[2] - ns[0] <= 2 ? SynthTones.RiseSmall : SynthTones.RiseBig;
  } else if (ns[0] >= ns[1] && ns[1] >= ns[2] && ns[0] > ns[2]) {
    // all falling - small or big
    return ns[0] - ns[2] <= 2 ? SynthTones.FallSmall : SynthTones.FallBig;
  }

  if (ns[0] < ns[1] && ns[1] > ns[2]) {
    // mountain
    return SynthTones.Mountain;
  } else if (ns[0] > ns[1] && ns[1] < ns[2]) {
    // valley
    return SynthTones.Valley;
  }
  return tone[0];
}

/** convert to an array of tone numbers 1-5 */
function asNumbers(tone: IpaType): number[] {
  const n: number[] = [];
  for (let i = 0; i < tone.length && i < 3; i++) {
    n.push(0x2ea - tone.charCodeAt(i));
  }
  return n;
}


/** move tones from after a consonant to after the previous vowel,
 * which allows us to distribute tones across fake diphthongs */
function moveTones(phonemes: PhonemeItem[]): PhonemeItem[] {
  const encoded = encodeForMove(phonemes);
  return moveEncodedTones(phonemes, encoded);
}
// v:vowelish (including approximant & length), t: tone, null: anything else
type EncodedForMove = ('v' | 't' | null);

/** encode just the feature details we care about: vowelish phonemes & tones */
function encodeForMove(phonemes: PhonemeItem[]): EncodedForMove[] {
  const encoded = phonemes.map((p, i) => {
    if (p.meta === undefined) {
      if (isTone(p.phoneme)) {
        return 't';
      }
      const features = ipaToFeatures(p.phoneme);
      if (features?.includes('vowel') || features?.includes('length')
        || features?.includes('approximant') || features?.includes('lateral-approximant')
        || (features?.includes('syllabic') && features?.includes('consonant'))
      ) {
        return 'v';
      }
    }
    return null;
  });
  return encoded;
}

function moveEncodedTones(phonemes: PhonemeItem[], encoded: EncodedForMove[]): PhonemeItem[] {
  const newPhonemes: PhonemeItem[] = [];
  for (let i = 0; i < phonemes.length; i++) {
    if (encoded[i] === 't' && encoded[i - 1] === null) {
      // don't copy a tone that follows anything other than a vowelish
    } else if (encoded[i] === 'v') {
      newPhonemes.push(phonemes[i]);
      // if we have VCT, VVCCT, etc., copy the tone after the last vowelish phoneme
      const end = checkEndOfSeries(encoded, i + 1);
      if (end !== null && encoded[end] === 't') {
        newPhonemes.push(phonemes[end]);
      }
    } else {
      newPhonemes.push(phonemes[i]);
    }
  }
  return newPhonemes;
}

/** if there's at least one non-(vowel or tone), return the end of the series */
function checkEndOfSeries(encoded: EncodedForMove[], i: number): number | null {
  if (encoded[i] !== null) {
    return null;  // no series
  }
  while (encoded[i] === null) { i++; }
  return i;
}


/** distribute tones across fake diphthongs */
function distributeTones(phonemes: PhonemeItem[]): PhonemeItem[] {
  const encoded = encodeForDistribute(phonemes);
  if (!encoded.includes('n') || !encoded.includes('t')) {
    return phonemes;  // nothing to do
  }
  return distributeByFeatures(phonemes, encoded);
}
// v:syllabic-vowel, n:non-syllabic-vowel or approximant, t: tone, #: length number
type EncodedForDistribute = ('v' | 'n' | 't' | '#' | null);

/** encode just the feature details we care about: fake diphthongs with a tone & optional length */
function encodeForDistribute(phonemes: PhonemeItem[]): EncodedForDistribute[] {
  const encoded = phonemes.map((p, i) => {
    if (p.meta === undefined) {
      const features = ipaToFeatures(p.phoneme);
      if (features?.includes('vowel')) {
        if (features.includes('non-syllabic') && i > 0) {
          // if this is part of a built-in diphthong, ignore it
          const dip = phonemes[i - 1].phoneme + p.phoneme;
          return ipaToSynthSymbols[dip] === undefined ? 'n' : null;
        }
        return 'v';
      } else if ((features?.includes('approximant') || features?.includes('lateral-approximant'))
        && !features.includes('syllabic')
      ) {
        return 'n';
      } else if (features?.includes('tone')) {
        return 't';
      } else if (features?.includes('length')) {
        return '#';
      }
    }
    return null;
  });
  return encoded;
}

/** figure out how to distribute the tones */
function distributeByFeatures(phonemes: PhonemeItem[], encoded: EncodedForDistribute[]) {
  const newPhonemes: PhonemeItem[] = [];
  for (let i = 0; i < phonemes.length; i++) {
    newPhonemes.push(phonemes[i]);
    if (checkFeatures(encoded, i, ['v', 'n', 't'])) {
      // copy tone: vnt -> vtnt
      newPhonemes.push(phonemes[i + 2]);
    } else if (checkFeatures(encoded, i, ['v', 'n', '#', 't'])) {
      // copy tone: vn#t -> vtn#t
      newPhonemes.push(phonemes[i + 3]);
    } else if (checkFeatures(encoded, i - 1, ['n', 't'])
      || checkFeatures(encoded, i - 2, ['n', '#', 't'])) {
      // if there are multiple tones, only keep the last one on the short vowel
      const l = phonemes[i].phoneme.length;
      if (l > 1) {
        newPhonemes[newPhonemes.length - 1] = { phoneme: phonemes[i].phoneme[l - 1] };
      }
    }
  }
  return newPhonemes;
}

/** check if dipFeatures[start]+ matches the list of expected features */
function checkFeatures(encoded: EncodedForDistribute[], start: number, features: string[]): boolean {
  return features.every((f, i) => encoded[i + start] === f);
}
